import { Controller } from "@hotwired/stimulus"

// Clamps long content to `maxHeight` pixels and reveals a toggle button only
// when the content actually overflows. Without JS the content stays fully open.
// Opening and closing animate the height when the content has a max-height
// transition (none under prefers-reduced-motion); the button's aria-expanded
// lets the stylesheet turn its caret.
export default class extends Controller {
  static targets = ["content", "button", "label"]
  static classes = ["collapsed"]
  static values = {
    maxHeight: { type: Number, default: 320 },
    moreLabel: String,
    lessLabel: String
  }

  connect () {
    this.expanded = false
    if (this.contentTarget.scrollHeight <= this.maxHeightValue) return

    this.contentTarget.style.maxHeight = `${this.maxHeightValue}px`
    this.contentTarget.classList.add(this.collapsedClass)
    this.buttonTarget.hidden = false
    this.render()
  }

  toggle () {
    this.expanded = !this.expanded
    this.animate()
    this.render()
  }

  // max-height cannot transition to `none`: go to the content's real height,
  // then drop the limit once open so the content can still grow (resize, fonts).
  animate () {
    const content = this.contentTarget
    const from = content.getBoundingClientRect().height
    const to = this.expanded ? content.scrollHeight : this.maxHeightValue

    this.stopWaiting()
    content.style.maxHeight = `${from}px`
    content.getBoundingClientRect() // commits the start height before the transition
    if (!this.expanded) content.classList.add(this.collapsedClass)
    content.style.maxHeight = `${to}px`

    const expanded = this.expanded
    this.finish = () => {
      this.stopWaiting()
      if (!expanded) return

      content.classList.remove(this.collapsedClass)
      content.style.maxHeight = ""
    }

    const duration = this.transitionDuration(content)
    if (duration === 0) {
      this.finish()
      return
    }

    content.addEventListener("transitionend", this.finish, { once: true })
    // transitionend never comes when the transition does not run (background tab, interrupted).
    this.fallback = setTimeout(this.finish, duration * 1000 + 50)
  }

  disconnect () {
    this.stopWaiting()
  }

  stopWaiting () {
    clearTimeout(this.fallback)
    if (this.finish) this.contentTarget.removeEventListener("transitionend", this.finish)
  }

  transitionDuration (element) {
    return parseFloat(window.getComputedStyle(element).transitionDuration) || 0
  }

  render () {
    this.buttonTarget.setAttribute("aria-expanded", String(this.expanded))
    this.labelTarget.textContent = this.expanded ? this.lessLabelValue : this.moreLabelValue
  }
}
